import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DOC_SECTIONS, docStatus } from '../components/operations/documents/docCatalog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Charge les documents d'une opération + statut des bons signés,
 * et calcule la conformité documentaire (catalogue fixe).
 */
export function useOperationDocuments(operationId, entityKind = 'operation') {
  const [documents, setDocuments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!operationId) return;
    setLoading(true);
    const notesUrl = entityKind === 'transfer'
      ? `${API}/transfers/${operationId}/loading-notes`
      : `${API}/operations/${operationId}/loading-notes`;
    try {
      const [docsRes, notesRes] = await Promise.allSettled([
        axios.get(`${API}/operations/${operationId}/documents`),
        axios.get(notesUrl),
      ]);
      setDocuments(docsRes.status === 'fulfilled' ? (docsRes.value.data || []) : []);
      const nd = notesRes.status === 'fulfilled' ? notesRes.value.data : null;
      setNotes(nd?.loadingNotes || nd?.notes || (Array.isArray(nd) ? nd : []));
    } finally {
      setLoading(false);
    }
  }, [operationId, entityKind]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const docsByType = (type) =>
    documents.filter((d) => d.documentType === type).sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));

  // Section D — bons signés
  const loadingNote = notes.find((n) => n.type === 'loading') || null;
  const unloadingNote = notes.find((n) => n.type === 'unloading') || null;
  const signItems = [
    { key: 'loading', label: 'Bon de chargement', note: loadingNote },
    { key: 'unloading', label: 'Bon de déchargement', note: unloadingNote },
  ];

  // Comptage
  let requis = 0, recus = 0, valides = 0, aVerifier = 0, refuses = 0, manquantsList = [];
  let requiredMissingOrRejected = false, requiredPending = false;

  DOC_SECTIONS.forEach((section) => {
    section.docs.forEach((d) => {
      requis += 1;
      const st = docStatus(docsByType(d.type));
      if (st === 'manquant') {
        manquantsList.push(d.label);
        if (d.required) requiredMissingOrRejected = true;
      } else {
        recus += 1;
        if (st === 'valide') valides += 1;
        else if (st === 'refuse') { refuses += 1; if (d.required) requiredMissingOrRejected = true; }
        else { aVerifier += 1; if (d.required) requiredPending = true; }
      }
    });
  });
  // Section D (2 bons signés)
  signItems.forEach((s) => {
    requis += 1;
    if (s.note) {
      recus += 1;
      if (s.note.isSigned) valides += 1;
      else { aVerifier += 1; requiredPending = true; }
    } else {
      manquantsList.push(s.label);
      requiredMissingOrRejected = true;
    }
  });

  const manquants = requis - recus;
  const progress = requis > 0 ? Math.round((recus / requis) * 100) : 0;
  const uniqueManquants = Array.from(new Set(manquantsList));
  let globalStatus = 'conforme';
  if (requiredMissingOrRejected) globalStatus = 'non_conforme';
  else if (requiredPending) globalStatus = 'a_verifier';

  const stats = { requis, recus, manquants, valides, aVerifier, refuses, progress, globalStatus, manquantsList: uniqueManquants };

  return { documents, notes, loading, docsByType, signItems, stats, refetch: fetchAll };
}
